import type { CloseEndedQuestion } from '@ilteducation/exercise-service-types';
import type { GetQuizterResponse } from '@ilteducation/quizter-types';
import { useVideos } from '@ilteducation/videos';
import { useUserContext } from '@ilteducation/ilt-auth';
import { useQuizterStore } from '../stores/quizterStore';
import { useVideoQuestions } from './useVideoQuestions';

export type SelectedVideo = {
  resourceId: string;
  title: string;
  thumbnail?: string;
  selectedQuestions?: CloseEndedQuestion[];
};

export type LoadedQuizter = {
  activityId: string;
  videos: SelectedVideo[];
  name: string;
  description: string;
};

/**
 * Builds a stable signature for a quiz question from its phrase and answer options.
 */
function questionSignature(question: CloseEndedQuestion): string {
  const phrase = question.phrase.trim();
  const optionValues = (question.options ?? []).map((option) => option.value.trim()).sort();
  const correctValue =
    question.options?.find((option) => option.id === question.answer)?.value.trim() ??
    question.options?.find((option) => option.value === question.answer)?.value.trim() ??
    '';
  return JSON.stringify([phrase, optionValues, correctValue]);
}

/**
 * Loads an existing quizter into the shape the builder consumes: resolves each resource's
 * video metadata and quiz questions, and pre-selects the subset that was saved.
 */
export function useQuizterLoader() {
  const quizterStore = useQuizterStore();
  const { getVideoById } = useVideos();
  const { realm } = useUserContext();

  // Resource ids are the raw video id suffixed with `-${realm}`; getVideoById expects the raw id.
  const toVideoId = (resourceId: string) => {
    const suffix = `-${realm}`;
    return resourceId.endsWith(suffix) ? resourceId.slice(0, -suffix.length) : resourceId;
  };

  const buildLoadedQuizter = async (quizter: GetQuizterResponse): Promise<LoadedQuizter> => {
    // Saved question ids don't match the exercise service's ids, so reconcile the subset by a
    // text signature of each question (phrase + answers).
    const savedSignatures = new Set(quizter.questions.map(questionSignature));

    const videos = await Promise.all(
      quizter.resourceIds.map(async (resourceId): Promise<SelectedVideo> => {
        const video = await getVideoById(toVideoId(resourceId)).catch(() => undefined);
        const { questions, fetch } = useVideoQuestions(resourceId);
        await fetch();
        const matched = questions.value.filter((q) => savedSignatures.has(questionSignature(q)));
        return {
          resourceId,
          title: video?.title ?? '',
          thumbnail: video?.thumbnail,
          selectedQuestions: matched.length > 0 ? matched : undefined,
        };
      }),
    );

    return {
      activityId: quizter.activityId,
      videos,
      name: quizter.name ?? '',
      description: quizter.description ?? '',
    };
  };

  const loadQuizter = async (activityId: string): Promise<LoadedQuizter> => {
    return buildLoadedQuizter(await quizterStore.getQuizter(activityId));
  };

  const loadSharedQuizter = async (shareId: string): Promise<LoadedQuizter> => {
    return buildLoadedQuizter(await quizterStore.getSharedQuizter(shareId));
  };

  return { loadQuizter, loadSharedQuizter };
}
